// Counterpart of webapp/src/store/contents.ts — the block cache for card
// content blocks. Upstream loads these lazily per card when CardDetail opens
// (mutator.ts / cardDetailContents.tsx); the clone does the same.

import {create} from 'zustand'
import {Block} from '../types/block'
import client from '../api/client'

const NON_CONTENT_TYPES = ['card', 'view', 'comment']

type ContentsState = {
    contents: Record<string, Block>
    loadedForCardIds: Record<string, boolean>
    loadBlocksOfCard: (cardId: string) => Promise<Block[]>
    loadBlock: (blockId: string, boardId: string) => Promise<Block | undefined>
    upsertBlocks: (blocks: Block[]) => void
    upsertBlock: (block: Block) => void
    removeBlock: (blockId: string) => void
    archiveBlock: (blockId: string, deleteAt: number) => void
}

export const useContents = create<ContentsState>((set, get) => ({
    contents: {},
    loadedForCardIds: {},

    async loadBlocksOfCard(cardId) {
        const blocks = await client.getBlocksWithParent(cardId)
        const content = blocks.filter((b) => !NON_CONTENT_TYPES.includes(b.type))
        get().upsertBlocks(content)
        set((state) => ({loadedForCardIds: {...state.loadedForCardIds, [cardId]: true}}))
        return content
    },

    async loadBlock(blockId, boardId) {
        const existing = get().contents[blockId]
        if (existing) {
            return existing
        }
        const blocks = await client.getBlocksWithBlockId(blockId, boardId)
        const block = blocks[0]
        if (block) {
            get().upsertBlock(block)
        }
        return block
    },

    upsertBlocks(blocks) {
        set((state) => {
            const next = {...state.contents}
            blocks.forEach((b) => {
                next[b.id] = b
            })
            return {contents: next}
        })
    },

    upsertBlock(block) {
        get().upsertBlocks([block])
    },

    removeBlock(blockId) {
        set((state) => {
            const contents = {...state.contents}
            delete contents[blockId]
            return {contents}
        })
    },

    archiveBlock(blockId, deleteAt) {
        const block = get().contents[blockId]
        if (block) {
            get().upsertBlock({...block, deleteAt})
        }
    },
}))

/** Ordered blocks for a card, following its contentOrder like upstream. */
export function selectBlockContents(state: ContentsState, contentOrder: Array<string | string[]>): Block[] {
    const out: Block[] = []
    contentOrder.forEach((id) => {
        const ids = typeof id === 'string' ? [id] : id
        ids.forEach((childId) => {
            const block = state.contents[childId]
            if (block && block.deleteAt === 0) {
                out.push(block)
            }
        })
    })
    return out
}

export function selectBlock(state: ContentsState, blockId: string | undefined): Block | undefined {
    return blockId ? state.contents[blockId] : undefined
}
